import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';

import {
  Menu as MenuIcon,
  People,
  Inventory2,
  EventNote,
  FitnessCenter,
  DarkMode,
  LightMode,
  Logout,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';

import { useAuthStore } from '../stores/auth.store';
import { Role } from '../types/auth.type';
import logoImage from '../assest/image/Logo.png';

const drawerWidth = 280;
const appBarHeight = 64;

interface MenuSubItem {
  text: string;
  path: string;
  roles?: Role[];
}

interface MenuItemConfig {
  text: string;
  icon: React.ComponentType;
  path: string;
  roles: Role[];
  subItems?: MenuSubItem[];
}

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
}

// Utility function to check if a path matches the current location
const isPathActive = (
  path: string,
  currentPath: string,
  subItems?: MenuSubItem[]
): boolean => {
  if (path === currentPath) return true;
  return subItems?.some((s) => currentPath.startsWith(s.path)) ?? false;
};

// Utility function to check if any sub-item path matches
const hasActiveSubItem = (
  subItems: MenuSubItem[],
  currentPath: string
): boolean => {
  return subItems.some((s) => currentPath.startsWith(s.path));
};

// Menu Item Component for better performance
const MenuItemComponent: React.FC<{
  item: MenuItemConfig;
  location: { pathname: string };
  openMenus: Record<string, boolean>;
  onToggleMenu: (text: string) => void;
  onNavigate: (path: string) => void;
  userRoles: Role[];
}> = React.memo(({ item, location, openMenus, onToggleMenu, onNavigate, userRoles }) => {
  const visibleSubItems = item.subItems?.filter(
    (sub) => !sub.roles || sub.roles.some((role) => userRoles.includes(role))
  );
  const active = isPathActive(item.path, location.pathname, visibleSubItems);
  const IconComponent = item.icon;

  return (
    <Box>
      <ListItemButton
        selected={active}
        onClick={() =>
          visibleSubItems?.length ? onToggleMenu(item.text) : onNavigate(item.path)
        }
        sx={{ borderRadius: 2, mb: 0.5 }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <IconComponent />
        </ListItemIcon>
        <ListItemText primary={item.text} />
        {visibleSubItems?.length ? (
          openMenus[item.text] ? <ExpandLess /> : <ExpandMore />
        ) : null}
      </ListItemButton>

      {visibleSubItems && visibleSubItems.length > 0 && (
        <Collapse in={openMenus[item.text]}>
          <List disablePadding>
            {visibleSubItems.map((sub) => (
              <ListItemButton
                key={sub.path}
                sx={{ pl: 6, borderRadius: 2 }}
                selected={isPathActive(sub.path, location.pathname)}
                onClick={() => onNavigate(sub.path)}
              >
                <ListItemText primary={sub.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
});

MenuItemComponent.displayName = 'MenuItemComponent';

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, isAdmin, isManager, userRoles } = useAuthStore();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const menuItems = useMemo<MenuItemConfig[]>(
    () => [
      { text: 'Users', icon: People, path: '/users', roles: [Role.ADMIN] },
      { text: 'Units', icon: Inventory2, path: '/units', roles: [Role.ADMIN] },
      { text: 'Schedule', icon: EventNote, path: '/schedule', roles: [Role.ADMIN, Role.MANAGER] },
      {
        text: 'Workouts',
        icon: FitnessCenter,
        path: '/workouts',
        roles: [Role.ADMIN, Role.COACH],
        subItems: [
          { text: 'Upload Videos', path: '/workouts', roles: [Role.ADMIN, Role.COACH] },
          { text: 'Catalog Videos', path: '/workout-catalog', roles: [Role.ADMIN] },
        ],
      },
    ],
    []
  );

  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) =>
        item.roles.some((role) => userRoles.includes(role))
      ),
    [menuItems, userRoles]
  );

  useEffect(() => {
    const state: Record<string, boolean> = {};
    menuItems.forEach((m) => {
      if (m.subItems && hasActiveSubItem(m.subItems, location.pathname)) {
        state[m.text] = true;
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...state }));
  }, [location.pathname, menuItems]);

  const handleToggleMenu = useCallback(
    (text: string) => {
      setOpenMenus((p) => ({ ...p, [text]: !p[text] }));
    },
    []
  );

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      setMobileOpen(false);
    },
    [navigate]
  );

  const handleProfileClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setProfileAnchor(e.currentTarget);
    },
    []
  );

  const handleProfileClose = useCallback(() => {
    setProfileAnchor(null);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    handleProfileClose();
  }, [logout, handleProfileClose]);

  const drawer = useMemo(
    () => (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ height: appBarHeight }}>
          <Box
            component="img"
            src={logoImage}
            alt="Logo"
            sx={{
              height: 40,
              width: 'auto',
              mr: 1,
              objectFit: 'contain',
            }}
          />
          <Typography fontWeight={700}>
            {isAdmin ? "Yan's Admin" : isManager ? "Yan's Manager" : "Yan's Coach"}
          </Typography>
        </Toolbar>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
          <List>
            {visibleMenuItems.map((item) => (
              <MenuItemComponent
                key={item.text}
                item={item}
                location={location}
                openMenus={openMenus}
                onToggleMenu={handleToggleMenu}
                onNavigate={handleNavigate}
                userRoles={userRoles}
              />
            ))}
          </List>
        </Box>
      </Box>
    ),
    [visibleMenuItems, location, openMenus, handleToggleMenu, handleNavigate, isAdmin, isManager, userRoles]
  );

  // Drawer common styles
  const drawerCommonStyles = useMemo(
    () => ({
      width: drawerWidth,
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box' as const,
      },
    }),
    []
  );

  const userInitial = useMemo(
    () => user?.username?.[0]?.toUpperCase() || '',
    [user?.username]
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{ height: appBarHeight }}>
        <Toolbar>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { sm: 'none' } }}
            aria-label="open drawer"
          >
            <MenuIcon />
          </IconButton>

          <Box flexGrow={1} />

          <IconButton onClick={onThemeToggle} aria-label="toggle theme">
            {theme.palette.mode === 'dark' ? <LightMode /> : <DarkMode />}
          </IconButton>

          <Box
            onClick={handleProfileClick}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ml: 2 }}
          >
            <Avatar sx={{ mr: 1 }}>{userInitial}</Avatar>
            <Typography variant="body2">{user?.username}</Typography>
          </Box>

          <Menu
            anchorEl={profileAnchor}
            open={!!profileAnchor}
            onClose={handleProfileClose}
          >
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, pt: `${appBarHeight}px` }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { sm: 'none' }, ...drawerCommonStyles }}
          ModalProps={{ keepMounted: true }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            flexShrink: 0,
            ...drawerCommonStyles,
          }}
        >
          {drawer}
        </Drawer>

        <Box
          component="main"
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            bgcolor: theme.palette.background.default,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
